import { getSupabaseClient } from "./index";

function apiBaseUrl(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return (env?.VITE_API_BASE_URL || "").replace(/\/$/, "");
}

async function authHeaders(): Promise<Record<string, string>> {
  const supabase = getSupabaseClient();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

async function adminFetch<T>(
  path: string,
  init?: RequestInit & { raw?: boolean }
): Promise<T> {
  const base = apiBaseUrl();
  if (!base) throw new Error("VITE_API_BASE_URL is not configured");
  const headers = await authHeaders();
  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...headers,
      ...(init?.headers || {}),
    },
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  if (init?.raw) {
    return (await res.text()) as T;
  }

  const contentType = res.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as T;
}

function supabasePublicUrl(): string {
  const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  return (env?.VITE_SUPABASE_URL || "").replace(/\/$/, "");
}

/** Production API may not have newer /v1/admin/drivers/* routes yet. */
function shouldFallbackToDirect(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();
  return (
    /\(404\)/.test(msg) ||
    lower.includes("not found") ||
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("load failed") ||
    lower.includes("network request failed") ||
    lower.includes("err_http2")
  );
}

function rpcErrorMessage(error: { message?: string; code?: string } | null): string {
  const msg = String(error?.message ?? "unknown_error");
  if (error?.code === "PGRST202" || /could not find the function/i.test(msg)) {
    return "rpc_missing";
  }
  return msg;
}

async function fileToBase64Payload(file: File): Promise<{ mime: string; data: string; filename: string }> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
  const data = dataUrl.includes(",") ? (dataUrl.split(",")[1] ?? dataUrl) : dataUrl;
  return {
    mime: file.type || "image/jpeg",
    data,
    filename: file.name || "kyc.jpg",
  };
}

async function uploadAdminDriverKycViaStorage(input: {
  kind: "ic" | "license" | "home_id";
  file: File;
}): Promise<{ ok: boolean; url: string; path: string }> {
  const supabase = getSupabaseClient();
  const mime = input.file.type || "image/jpeg";
  const ext = mime.includes("png") ? "png" : mime.includes("webp") ? "webp" : "jpg";
  const path = `${input.kind}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("driver-kyc").upload(path, input.file, {
    cacheControl: "3600",
    upsert: false,
    contentType: mime,
  });
  if (error) {
    const text = `${error.message} ${error.name}`;
    if (/bucket/i.test(text) && /not found|does not exist/i.test(text)) {
      throw new Error("kyc_bucket_missing");
    }
    if (/row-level security|unauthorized|not allowed/i.test(text)) {
      throw new Error("kyc_storage_denied");
    }
    throw new Error(error.message || "kyc_upload_failed");
  }
  const base = supabasePublicUrl();
  const url = base
    ? `${base}/storage/v1/object/driver-kyc/${path}`
    : supabase.storage.from("driver-kyc").getPublicUrl(path).data.publicUrl;
  return { ok: true, url, path };
}

export type AdminOverview = {
  stats: Record<string, number>;
  charts: {
    ordersOverTime: Array<{ day: string; orders: number; gmv: number }>;
    revenueOverTime: Array<{ day: string; revenue: number }>;
    creditsConsumedOverTime: Array<{ day: string; consumed: number }>;
  };
  topRestaurants: Array<{
    id: string;
    name: string;
    status: string;
    creditBalance: number;
    orderCount: number;
  }>;
  recentActivity: Array<{
    id: string;
    actor_label: string | null;
    action: string;
    summary: string;
    created_at: string;
  }>;
};

export type Paginated<T> = {
  page: number;
  limit: number;
  total: number;
  items: T[];
};

export type AdminClient = {
  id: string;
  name: string;
  slug: string;
  status: string;
  order_count?: number;
  customer_count?: number;
  driver_count?: number;
  phone?: string | null;
  logo_url?: string | null;
  /** Reserved client login username (login ID). */
  username?: string | null;
  /** Owner contact email — optional, used later for invoices (never a login ID). */
  owner_email?: string | null;
  created_at: string;
  currency?: string;
  /**
   * Wallet balance used for billing: the client wallet, or the consolidated
   * partner wallet for partner-managed clients (see billing_payer).
   */
  wallet_balance?: number;
  /** Who pays the per-trip fee: client wallet or consolidated partner wallet. */
  billing_payer?: "client" | "partner";
  /** Completed driver trips (delivered/completed with an assigned driver). */
  completed_trips?: number;
  /** SA-1 SoT: oelox | partner */
  client_type?: "oelox" | "partner";
  controlling_partner_id?: string | null;
  controlling_partner_name?: string | null;
  client_type_status?: "confirmed" | "needs_review" | "manual_override";
  client_type_classified_at?: string | null;
  client_type_classified_by?: string | null;
  /** @deprecated Alias of client_type for older UI; prefer client_type */
  client_kind?: "oelox" | "partner_linked";
  active_partner_links?: number;
  partner_names?: string | null;
};

export async function fetchAdminOverview() {
  return adminFetch<AdminOverview>("/v1/admin/overview");
}

export async function fetchAdminClients(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  kind?: "oelox" | "partner_linked" | "partner" | "needs_review" | "";
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.search) q.set("search", params.search);
  if (params?.status) q.set("status", params.status);
  if (params?.kind) q.set("kind", params.kind);
  const qs = q.toString();
  return adminFetch<Paginated<AdminClient>>(`/v1/admin/clients${qs ? `?${qs}` : ""}`);
}

export async function fetchAdminClient(id: string) {
  return adminFetch<{
    client: AdminClient & Record<string, unknown>;
    /** Trip-based billing resolution for this client (no plans, no credits). */
    billing?: {
      payer: "client" | "partner";
      per_trip_fee_iqd: number | null;
      wallet: {
        balance: number;
        negative_credit_limit: number;
        alert_threshold_iqd: number;
      } | null;
    };
    partnerLinks?: Array<Record<string, unknown>>;
    clientTypeAudit?: Array<Record<string, unknown>>;
    recentOrders: unknown[];
  }>(`/v1/admin/clients/${id}`);
}

export async function updateAdminClientStatus(id: string, status: string) {
  return adminFetch<{ client: AdminClient }>(`/v1/admin/clients/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function updateAdminClientType(
  id: string,
  input: {
    clientType: "oelox" | "partner";
    controllingPartnerId?: string | null;
    reason?: string;
  }
) {
  return adminFetch<{
    ok: boolean;
    tenant_id: string;
    client_type: string;
    controlling_partner_id: string | null;
    client_type_status: string;
  }>(`/v1/admin/clients/${id}/client-type`, {
    method: "PATCH",
    body: JSON.stringify({
      clientType: input.clientType,
      controllingPartnerId: input.controllingPartnerId ?? null,
      reason: input.reason,
    }),
  });
}

export async function adjustAdminClientCredits(
  id: string,
  input: { amount: number; direction: "add" | "deduct"; note?: string }
) {
  return adminFetch<{ balanceAfter: number; transactionId: string }>(
    `/v1/admin/clients/${id}/credits`,
    { method: "POST", body: JSON.stringify(input) }
  );
}

export async function fetchGlobalCustomers(params?: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.search) q.set("search", params.search);
  const qs = q.toString();
  return adminFetch<Paginated<Record<string, unknown>>>(
    `/v1/admin/customers/global${qs ? `?${qs}` : ""}`
  );
}

export async function fetchGlobalCustomer(id: string) {
  return adminFetch<{ customer: Record<string, unknown>; orders: unknown[] }>(
    `/v1/admin/customers/global/${id}`
  );
}

export async function fetchRestaurantCustomers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  tenantId?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.search) q.set("search", params.search);
  if (params?.tenantId) q.set("tenantId", params.tenantId);
  const qs = q.toString();
  return adminFetch<Paginated<Record<string, unknown>>>(
    `/v1/admin/customers/restaurant${qs ? `?${qs}` : ""}`
  );
}

export async function fetchRestaurantCustomer(id: string) {
  return adminFetch<{ customer: Record<string, unknown>; orders: unknown[] }>(
    `/v1/admin/customers/restaurant/${id}`
  );
}

export type AdminOrder = {
  id: string;
  tenant_id: string;
  restaurant_name: string;
  status: string;
  total: number;
  currency: string;
  source?: string | null;
  channel?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  created_at: string;
  assigned_driver_id?: string | null;
  driver_name?: string | null;
  handling_partner_id?: string | null;
  partner_name?: string | null;
  partner_accepted_at?: string | null;
  partner_declined_at?: string | null;
  partner_stage?: string | null;
  return_to_store?: boolean | null;
  rejected_at?: string | null;
  rejection_charge_delivery?: boolean | null;
  rejection_keep_driver_fee?: boolean | null;
};

export async function fetchAdminOrders(params?: {
  page?: number;
  limit?: number;
  status?: string;
  tenantId?: string;
  search?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.status) q.set("status", params.status);
  if (params?.tenantId) q.set("tenantId", params.tenantId);
  if (params?.search) q.set("search", params.search);
  const qs = q.toString();
  return adminFetch<Paginated<AdminOrder>>(`/v1/admin/orders${qs ? `?${qs}` : ""}`);
}

// ── Order desk (Phase 2) ─────────────────────────────────────────────────────

export type AdminOrderDetail = {
  order: Record<string, unknown>;
  tenant: Record<string, unknown> | null;
  partner: Record<string, unknown> | null;
  driver: Record<string, unknown> | null;
  settlement: Record<string, unknown> | null;
  ledger: Record<string, unknown> | null;
  wallets: Record<string, unknown>;
  events: Array<Record<string, unknown>>;
  dispatch_events: Array<Record<string, unknown>>;
};

export async function fetchAdminOrderDetail(id: string) {
  return adminFetch<AdminOrderDetail>(`/v1/admin/orders/${id}`);
}

export async function adminReassignOrderDriver(
  id: string,
  input: { driverId: string; reason?: string }
) {
  return adminFetch<Record<string, unknown>>(`/v1/admin/orders/${id}/reassign-driver`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function adminReblastOrder(
  id: string,
  input?: { target?: "auto" | "partner" | "global" | "business"; reason?: string }
) {
  return adminFetch<Record<string, unknown>>(`/v1/admin/orders/${id}/reblast`, {
    method: "POST",
    body: JSON.stringify(input ?? {}),
  });
}

export async function adminCancelOrder(id: string, input?: { reason?: string }) {
  return adminFetch<Record<string, unknown>>(`/v1/admin/orders/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify(input ?? {}),
  });
}

export async function adminForceCompleteOrder(
  id: string,
  input?: { reason?: string }
) {
  return adminFetch<Record<string, unknown>>(`/v1/admin/orders/${id}/force-complete`, {
    method: "POST",
    body: JSON.stringify(input ?? {}),
  });
}

export async function fetchAdminPayments(params?: {
  page?: number;
  limit?: number;
  status?: string;
  tenantId?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.status) q.set("status", params.status);
  if (params?.tenantId) q.set("tenantId", params.tenantId);
  const qs = q.toString();
  return adminFetch<Paginated<Record<string, unknown>>>(
    `/v1/admin/payments${qs ? `?${qs}` : ""}`
  );
}

export async function createAdminPayment(input: {
  tenantId: string;
  packageId?: string;
  amountIqd?: number;
  credits?: number;
  status?: "pending" | "paid" | "failed";
  method?: string;
  reference?: string;
  note?: string;
}) {
  return adminFetch<{ paymentId: string; balanceAfter?: number }>("/v1/admin/payments", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAdminPaymentStatus(id: string, status: string) {
  return adminFetch<{ ok: boolean }>(`/v1/admin/payments/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function fetchAdminPlans() {
  return adminFetch<{
    packages: Array<Record<string, unknown>>;
    pricingRules: Array<Record<string, unknown>>;
  }>("/v1/admin/plans");
}

export async function createAdminPackage(input: {
  name: string;
  credits: number;
  priceIqd: number;
  description?: string;
  sortOrder?: number;
  isActive?: boolean;
}) {
  return adminFetch<{ package: Record<string, unknown> }>("/v1/admin/plans/packages", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAdminPackage(
  id: string,
  input: Partial<{
    name: string;
    credits: number;
    priceIqd: number;
    description: string;
    sortOrder: number;
    isActive: boolean;
  }>
) {
  return adminFetch<{ package: Record<string, unknown> }>(`/v1/admin/plans/packages/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function createAdminPricingRule(input: {
  name: string;
  minOrderValue: number;
  maxOrderValue?: number | null;
  costIqd: number;
  sortOrder?: number;
  isActive?: boolean;
}) {
  return adminFetch<{ rule: Record<string, unknown> }>("/v1/admin/plans/pricing-rules", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAdminPricingRule(
  id: string,
  input: Partial<{
    name: string;
    minOrderValue: number;
    maxOrderValue: number | null;
    costIqd: number;
    sortOrder: number;
    isActive: boolean;
  }>
) {
  return adminFetch<{ rule: Record<string, unknown> }>(
    `/v1/admin/plans/pricing-rules/${id}`,
    { method: "PATCH", body: JSON.stringify(input) }
  );
}

export async function fetchAdminDrivers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  /** Stored: restaurant | oelox_network | partner. Aliases: client→restaurant, global→oelox_network */
  fleetType?: "restaurant" | "oelox_network" | "partner" | "client" | "global" | "";
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.search) q.set("search", params.search);
  if (params?.fleetType) q.set("fleetType", params.fleetType);
  const qs = q.toString();
  return adminFetch<{
    summary: {
      total: number;
      active: number;
      deliveries: number;
      network?: number;
      restaurant?: number;
      client?: number;
      partner?: number;
    };
    page: number;
    limit: number;
    total: number;
    items: Array<Record<string, unknown>>;
  }>(`/v1/admin/drivers${qs ? `?${qs}` : ""}`);
}

export async function uploadAdminDriverKyc(input: {
  kind: "ic" | "license" | "home_id";
  file: File;
  filename?: string;
  mime?: string;
  data?: string;
}) {
  const file = input.file;
  try {
    return await uploadAdminDriverKycViaStorage({ kind: input.kind, file });
  } catch (storageErr) {
    try {
      const payload = input.data
        ? {
            kind: input.kind,
            filename: input.filename || file.name,
            mime: input.mime || file.type,
            data: input.data,
          }
        : { kind: input.kind, ...(await fileToBase64Payload(file)) };
      return await adminFetch<{ ok: boolean; url: string; path: string }>(
        "/v1/admin/drivers/kyc-upload",
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );
    } catch (apiErr) {
      if (shouldFallbackToDirect(apiErr)) throw storageErr;
      throw apiErr;
    }
  }
}

export async function createAdminNetworkDriver(input: {
  name: string;
  phone: string;
  email?: string | null;
  vehicleType?: string;
  vehicleNumber: string;
  vehicleModel?: string | null;
  vehicleColor?: string | null;
  icImageUrl: string;
  licenseImageUrl: string;
  homeIdImageUrl?: string | null;
  collectsCod?: boolean;
  expiresHours?: number;
}) {
  const payload = {
    name: input.name,
    phone: input.phone,
    email: input.email ?? null,
    vehicle_type: input.vehicleType ?? "motorcycle",
    vehicle_number: input.vehicleNumber,
    vehicle_model: input.vehicleModel ?? null,
    vehicle_color: input.vehicleColor ?? null,
    ic_image_url: input.icImageUrl,
    license_image_url: input.licenseImageUrl,
    home_id_image_url: input.homeIdImageUrl ?? null,
    collects_cod: input.collectsCod ?? true,
    expires_hours: input.expiresHours ?? 72,
  };
  const supabase = getSupabaseClient();
  const rpc = await supabase.rpc("admin_create_oelox_network_driver", { p_payload: payload });
  if (!rpc.error) {
    const result = (rpc.data ?? {}) as {
      ok: boolean;
      driver: Record<string, unknown>;
      fleet_type: string;
      activation_code: string;
      expires_at?: string;
    };
    if (result?.ok === true && result.activation_code) return result;
  }
  try {
    return await adminFetch<{
      ok: boolean;
      driver: Record<string, unknown>;
      fleet_type: string;
      activation_code: string;
      expires_at?: string;
    }>("/v1/admin/drivers/network", {
      method: "POST",
      body: JSON.stringify(input),
    });
  } catch (err) {
    if (rpc.error) throw new Error(rpcErrorMessage(rpc.error));
    throw err;
  }
}

export async function setAdminDriverFleet(
  driverId: string,
  fleetType: "restaurant" | "oelox_network"
) {
  return adminFetch<{ ok: boolean; driver: Record<string, unknown> }>(
    `/v1/admin/drivers/${driverId}/fleet`,
    { method: "POST", body: JSON.stringify({ fleetType }) }
  );
}

export async function linkAdminDriverRestaurant(driverId: string, tenantId: string) {
  const supabase = getSupabaseClient();
  const rpc = await supabase.rpc("admin_link_driver_restaurant", {
    p_driver_id: driverId,
    p_tenant_id: tenantId,
  });
  if (!rpc.error) {
    return (rpc.data ?? { ok: true, link: {} }) as { ok: boolean; link: Record<string, unknown> };
  }
  try {
    return await adminFetch<{ ok: boolean; link: Record<string, unknown> }>(
      `/v1/admin/drivers/${driverId}/links`,
      { method: "POST", body: JSON.stringify({ tenantId }) }
    );
  } catch (err) {
    throw new Error(rpcErrorMessage(rpc.error) || (err instanceof Error ? err.message : "link_failed"));
  }
}

export async function unlinkAdminDriverRestaurant(driverId: string, tenantId: string) {
  return adminFetch<{ ok: boolean }>(
    `/v1/admin/drivers/${driverId}/links/${tenantId}`,
    { method: "DELETE" }
  );
}

export async function setAdminDriverActive(driverId: string, active: boolean) {
  const supabase = getSupabaseClient();
  const rpc = await supabase.rpc("admin_set_driver_active", {
    p_driver_id: driverId,
    p_active: active,
  });
  if (!rpc.error) {
    return (rpc.data ?? { ok: true, driver: {} }) as { ok: boolean; driver: Record<string, unknown> };
  }
  try {
    return await adminFetch<{ ok: boolean; driver: Record<string, unknown> }>(
      `/v1/admin/drivers/${driverId}/active`,
      { method: "POST", body: JSON.stringify({ active }) }
    );
  } catch (err) {
    throw new Error(rpcErrorMessage(rpc.error) || (err instanceof Error ? err.message : "update_failed"));
  }
}

export type AdminDriverDetail = {
  ok: boolean;
  driver: Record<string, unknown>;
  links: Array<Record<string, unknown>>;
  sessions: Array<Record<string, unknown>>;
  deliveries: Array<Record<string, unknown>>;
  activation_code: string | null;
  activation_expires_at: string | null;
};

export async function fetchAdminDriver(id: string) {
  return adminFetch<AdminDriverDetail>(`/v1/admin/drivers/${id}`);
}

export async function updateAdminDriver(
  id: string,
  patch: {
    name: string;
    phone: string;
    email?: string | null;
    address?: string | null;
    vehicleType?: string;
    vehicleNumber?: string | null;
    vehicleModel?: string | null;
    vehicleColor?: string | null;
    collectsCod?: boolean;
    isVerified?: boolean;
  }
) {
  return adminFetch<{ ok: boolean; driver: Record<string, unknown> }>(
    `/v1/admin/drivers/${id}`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
}

export async function mintAdminDriverActivationCode(id: string, expiresHours = 72) {
  return adminFetch<{ ok: boolean; passcode?: string; expires_at?: string }>(
    `/v1/admin/drivers/${id}/activation-code`,
    { method: "POST", body: JSON.stringify({ expiresHours }) }
  );
}

export async function fetchAdminDriverUpdateRequests(params?: {
  status?: "pending" | "approved" | "rejected" | "all";
  limit?: number;
}) {
  const supabase = getSupabaseClient();
  const rpc = await supabase.rpc("admin_list_driver_update_requests", {
    p_status: params?.status ?? "pending",
    p_limit: params?.limit ?? 50,
  });
  if (!rpc.error) {
    const result = (rpc.data ?? {}) as { ok?: boolean; items?: Array<Record<string, unknown>> };
    return { ok: true, items: result.items ?? [] };
  }
  const q = new URLSearchParams();
  if (params?.status) q.set("status", params.status);
  if (params?.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  try {
    return await adminFetch<{ ok: boolean; items: Array<Record<string, unknown>> }>(
      `/v1/admin/drivers/update-requests${qs ? `?${qs}` : ""}`
    );
  } catch {
    return { ok: true, items: [] as Array<Record<string, unknown>> };
  }
}

export async function resolveAdminDriverUpdateRequest(
  requestId: string,
  status: "approved" | "rejected",
  note?: string
) {
  const supabase = getSupabaseClient();
  const rpc = await supabase.rpc("admin_resolve_driver_update_request", {
    p_request_id: requestId,
    p_status: status,
    p_note: note ?? null,
  });
  if (!rpc.error) {
    return (rpc.data ?? { ok: true, request: {} }) as { ok: boolean; request: Record<string, unknown> };
  }
  try {
    return await adminFetch<{ ok: boolean; request: Record<string, unknown> }>(
      `/v1/admin/drivers/update-requests/${requestId}/resolve`,
      { method: "POST", body: JSON.stringify({ status, note }) }
    );
  } catch (err) {
    throw new Error(rpcErrorMessage(rpc.error) || (err instanceof Error ? err.message : "resolve_failed"));
  }
}

export async function fetchAdminDriverAudit(driverId: string) {
  const supabase = getSupabaseClient();
  const rpc = await supabase.rpc("admin_get_driver_audit", { p_driver_id: driverId });
  if (!rpc.error) {
    return (rpc.data ?? {
      ok: true,
      driver: {},
      deliveries: [],
      sessions: [],
      update_requests: [],
      recent_locations: [],
    }) as {
      ok: boolean;
      driver: Record<string, unknown>;
      deliveries: Array<Record<string, unknown>>;
      sessions: Array<Record<string, unknown>>;
      update_requests: Array<Record<string, unknown>>;
      recent_locations: Array<Record<string, unknown>>;
    };
  }
  try {
    return await adminFetch<{
      ok: boolean;
      driver: Record<string, unknown>;
      deliveries: Array<Record<string, unknown>>;
      sessions: Array<Record<string, unknown>>;
      update_requests: Array<Record<string, unknown>>;
      recent_locations: Array<Record<string, unknown>>;
    }>(`/v1/admin/drivers/${driverId}/audit`);
  } catch (err) {
    throw new Error(rpcErrorMessage(rpc.error) || (err instanceof Error ? err.message : "audit_failed"));
  }
}

export async function fetchAdminReports(params?: { from?: string; to?: string }) {
  const q = new URLSearchParams();
  if (params?.from) q.set("from", params.from);
  if (params?.to) q.set("to", params.to);
  const qs = q.toString();
  return adminFetch<{
    revenue: { totalIqd: number; paymentCount: number };
    usageByRestaurant: Array<{ id: string; name: string; orderCount: number; gmv: number }>;
    creditConsumption: Array<{ id: string; name: string; consumed: number }>;
    topCustomers: Array<Record<string, unknown>>;
  }>(`/v1/admin/reports${qs ? `?${qs}` : ""}`);
}

export async function exportAdminReportCsv(kind: "payments" | "credit_usage" | "orders") {
  const csv = await adminFetch<string>(`/v1/admin/reports/export.csv?kind=${kind}`, {
    raw: true,
  });
  return csv;
}

export async function fetchAdminSettings() {
  return adminFetch<{
    settings: Record<string, { value: unknown; description?: string; updatedAt?: string }>;
  }>("/v1/admin/settings");
}

export async function updateAdminSettings(settings: Record<string, unknown>) {
  return adminFetch<{ ok: boolean }>("/v1/admin/settings", {
    method: "PUT",
    body: JSON.stringify({ settings }),
  });
}

// ── OELOX identity (migration 185) ──────────────────────────────────────────
// Full platform identity: brand, support, legal/invoice fields and defaults
// (currency IQD default / USD, language ar default / ku / en, Asia/Baghdad).

export type AdminIdentity = {
  brand_name_en: string;
  brand_name_ar: string;
  brand_name_ku: string;
  logo_url: string | null;
  support_email: string | null;
  support_phone: string | null;
  website: string | null;
  legal_name: string;
  address: string;
  tax_number: string;
  invoice_footer: string;
  default_currency: "IQD" | "USD";
  supported_currencies: string[];
  default_language: "ar" | "ku" | "en";
  supported_languages: string[];
  timezone: string;
};

function mapAdminIdentity(raw: unknown): AdminIdentity {
  const row = (raw ?? {}) as Record<string, unknown>;
  const str = (v: unknown, fallback = "") => (v == null ? fallback : String(v));
  const nullable = (v: unknown) => (v == null || String(v).trim() === "" ? null : String(v));
  return {
    brand_name_en: str(row.brand_name_en, "OELOX"),
    brand_name_ar: str(row.brand_name_ar),
    brand_name_ku: str(row.brand_name_ku),
    logo_url: nullable(row.logo_url),
    support_email: nullable(row.support_email),
    support_phone: nullable(row.support_phone),
    website: nullable(row.website),
    legal_name: str(row.legal_name),
    address: str(row.address),
    tax_number: str(row.tax_number),
    invoice_footer: str(row.invoice_footer),
    default_currency: String(row.default_currency) === "USD" ? "USD" : "IQD",
    supported_currencies: Array.isArray(row.supported_currencies)
      ? row.supported_currencies.map(String)
      : ["IQD", "USD"],
    default_language:
      String(row.default_language) === "ku" || String(row.default_language) === "en"
        ? (String(row.default_language) as "ku" | "en")
        : "ar",
    supported_languages: Array.isArray(row.supported_languages)
      ? row.supported_languages.map(String)
      : ["ar", "ku", "en"],
    timezone: str(row.timezone, "Asia/Baghdad"),
  };
}

export async function fetchAdminIdentity(): Promise<AdminIdentity> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_get_identity");
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAdminIdentity(data);
}

export async function updateAdminIdentity(identity: AdminIdentity): Promise<AdminIdentity> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_update_identity", {
    p_identity: identity,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAdminIdentity(data);
}

/** Platform assets (identity logo) — public marketplace-assets bucket, `identity/` folder. */
export async function uploadPlatformAsset(file: File): Promise<string> {
  const maxBytes = 5 * 1024 * 1024;
  if (file.size > maxBytes) throw new Error("image_too_large");
  const supabase = getSupabaseClient();
  const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
  const path = `identity/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("marketplace-assets").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("marketplace-assets").getPublicUrl(path);
  return data.publicUrl;
}

// ── OELOX global delivery fees (platform bands for OELOX-direct drivers) ─────
export type OeloxFeeBand = {
  min_km: number;
  max_km: number;
  fee_amount: number;
  /** Per-band driver pay (IQD). null = legacy flat/commission fallback. */
  driver_amount: number | null;
  /** fee_amount − driver_amount (server-computed, read-only). */
  margin?: number | null;
  sort_order?: number;
};

function mapOeloxFeeBands(data: unknown): OeloxFeeBand[] {
  // RPCs return either a bare array (oelox_list) or { bands: [...] } (admin_get).
  const raw: unknown[] = Array.isArray(data)
    ? data
    : (((data as Record<string, unknown> | null)?.["bands"] as
        | unknown[]
        | undefined) ?? []);
  if (!Array.isArray(raw)) return [];
  return raw.map((rawBand) => {
    const row = (rawBand ?? {}) as Record<string, unknown>;
    const driver =
      row.driver_amount == null || row.driver_amount === ""
        ? null
        : Number(row.driver_amount);
    return {
      min_km: Number(row.min_km ?? 0),
      max_km: Number(row.max_km ?? 0),
      fee_amount: Number(row.fee_amount ?? 0),
      driver_amount: driver != null && Number.isFinite(driver) ? driver : null,
      margin: row.margin == null ? null : Number(row.margin),
      sort_order: Number(row.sort_order ?? 0),
    };
  });
}

export async function fetchOeloxFeeBands(): Promise<OeloxFeeBand[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("oelox_list_fee_bands");
  if (error) throw new Error(rpcErrorMessage(error));
  return mapOeloxFeeBands(data);
}

export async function updateOeloxFeeBands(bands: OeloxFeeBand[]): Promise<OeloxFeeBand[]> {
  const supabase = getSupabaseClient();
  const payload = bands.map((b, index) => ({
    min_km: b.min_km,
    max_km: b.max_km,
    fee_amount: b.fee_amount,
    driver_amount: b.driver_amount,
    sort_order: index,
  }));
  const { data, error } = await supabase.rpc("oelox_upsert_fee_bands", {
    p_bands: payload,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapOeloxFeeBands(data);
}

// ── Partner fee bands (super-admin view/override) ────────────────────────────
export async function fetchAdminPartnerPricing(
  partnerId: string
): Promise<{ bands: OeloxFeeBand[] }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_get_partner_fee_bands", {
    p_partner_id: partnerId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return { bands: mapOeloxFeeBands(data) };
}

export async function updateAdminPartnerFeeBands(
  partnerId: string,
  bands: OeloxFeeBand[]
): Promise<OeloxFeeBand[]> {
  const supabase = getSupabaseClient();
  const payload = bands.map((b, index) => ({
    min_km: b.min_km,
    max_km: b.max_km,
    fee_amount: b.fee_amount,
    driver_amount: b.driver_amount,
    sort_order: index,
  }));
  const { data, error } = await supabase.rpc("admin_update_partner_fee_bands", {
    p_partner_id: partnerId,
    p_bands: payload,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapOeloxFeeBands(data);
}

// ── Partner trip pricing (Group A) ───────────────────────────────────────────
// Partner scope  = the partner's own trip bands (what he charges his clients).
// Partner trip fee = what OELOX deducts from the partner per completed trip.


//   direct_client → fee debited from the client wallet (OELOX direct clients)
//   partner       → fee debited from the consolidated partner wallet
// The fee is charged per completed driver-handled trip; customer self-pickup /
// dine-in orders without a driver are never charged.

export type AdminBillingConfigCode = "direct_client" | "partner";

export type AdminBillingConfig = {
  code: AdminBillingConfigCode;
  name: string;
  description: string | null;
  per_trip_fee_iqd: number;
  is_active: boolean;
  updated_at: string | null;
};

export type AdminBillingConfigs = {
  configs: AdminBillingConfig[];
};

function mapAdminBillingConfig(raw: unknown): AdminBillingConfig {
  const row = (raw ?? {}) as Record<string, unknown>;
  return {
    code: (String(row.code) === "partner" ? "partner" : "direct_client") as AdminBillingConfigCode,
    name: String(row.name ?? ""),
    description: row.description != null ? String(row.description) : null,
    per_trip_fee_iqd: Number(row.per_trip_fee_iqd ?? 0),
    is_active: row.is_active !== false,
    updated_at: row.updated_at != null ? String(row.updated_at) : null,
  };
}

export async function fetchAdminBillingConfigs(): Promise<AdminBillingConfigs> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_list_billing_configs");
  if (error) throw new Error(rpcErrorMessage(error));
  const root = (data ?? {}) as Record<string, unknown>;
  const configs = Array.isArray(root.configs) ? root.configs : [];
  return { configs: configs.map(mapAdminBillingConfig) };
}

export async function updateAdminBillingConfig(
  code: AdminBillingConfigCode,
  perTripFeeIqd: number
): Promise<AdminBillingConfig> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_update_billing_config", {
    p_code: code,
    p_per_trip_fee_iqd: perTripFeeIqd,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAdminBillingConfig(data);
}

// ── Multi-wallet v2 (S1): client/partner wallets, limits, top-ups, order desk ─

export type AdminWalletType = "client" | "partner";

export type AdminWalletTransaction = {
  id: string;
  order_id: string | null;
  type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  created_at: string;
};

export type AdminWalletTotals = {
  tx_count: number;
  fees_total: number;
  topups_total: number;
};

export type AdminWalletSnapshot = {
  balance: number;
  currency: string;
  negative_credit_limit: number;
  alert_threshold_iqd: number;
  updated_at: string | null;
  transactions: AdminWalletTransaction[];
  totals: AdminWalletTotals;
};

function mapAdminWalletSnapshot(data: unknown): AdminWalletSnapshot {
  const root = (data ?? {}) as Record<string, unknown>;
  const wallet = (root.wallet ?? {}) as Record<string, unknown>;
  const txs = Array.isArray(root.transactions) ? root.transactions : [];
  const totals = (root.totals ?? {}) as Record<string, unknown>;
  return {
    balance: Number(wallet.balance ?? 0),
    currency: String(wallet.currency ?? "IQD"),
    negative_credit_limit: Number(wallet.negative_credit_limit ?? 0),
    alert_threshold_iqd: Number(wallet.alert_threshold_iqd ?? 10000),
    updated_at: wallet.updated_at != null ? String(wallet.updated_at) : null,
    totals: {
      tx_count: Number(totals.tx_count ?? 0),
      fees_total: Number(totals.fees_total ?? 0),
      topups_total: Number(totals.topups_total ?? 0),
    },
    transactions: txs.map((raw) => {
      const t = (raw ?? {}) as Record<string, unknown>;
      return {
        id: String(t.id ?? ""),
        order_id: t.order_id != null ? String(t.order_id) : null,
        type: String(t.type ?? ""),
        amount: Number(t.amount ?? 0),
        balance_after: Number(t.balance_after ?? 0),
        description: t.description != null ? String(t.description) : null,
        created_at: String(t.created_at ?? ""),
      };
    }),
  };
}

export async function fetchAdminWallet(
  walletType: AdminWalletType,
  ownerId: string
): Promise<AdminWalletSnapshot> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_wallet_get", {
    p_wallet_type: walletType,
    p_owner_id: ownerId,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAdminWalletSnapshot(data);
}

export async function adminWalletTopup(
  walletType: AdminWalletType,
  ownerId: string,
  amount: number,
  note?: string
): Promise<AdminWalletSnapshot> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_wallet_topup", {
    p_wallet_type: walletType,
    p_owner_id: ownerId,
    p_amount: amount,
    p_note: note?.trim() || null,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAdminWalletSnapshot(data);
}

export async function adminWalletSetLimits(
  walletType: AdminWalletType,
  ownerId: string,
  negativeCreditLimit: number,
  alertThresholdIqd?: number
): Promise<AdminWalletSnapshot> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_wallet_set_limits", {
    p_wallet_type: walletType,
    p_owner_id: ownerId,
    p_negative_credit_limit: negativeCreditLimit,
    p_alert_threshold_iqd: alertThresholdIqd ?? null,
  });
  if (error) throw new Error(rpcErrorMessage(error));
  return mapAdminWalletSnapshot(data);
}

export type AdminOrderWalletPayer = {
  wallet_type: string;
  owner_id: string | null;
  owner_name: string | null;
  balance: number;
  negative_credit_limit: number;
  alert_threshold_iqd: number;
};

export type AdminOrderWallets = {
  order_id: string;
  orders_driver_trip_fee: number | null;
  platform_fee: {
    amount: number;
    payer_wallet_type: string;
    payer_id: string;
    created_at: string;
  } | null;
  driver_settlement: Record<string, unknown> | null;
  payer_wallet: AdminOrderWalletPayer | null;
  driver_wallet: { wallet_type: string; balance: number } | null;
};

export async function fetchAdminOrderWallets(orderId: string): Promise<AdminOrderWallets> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("admin_order_wallets", { p_order_id: orderId });
  if (error) throw new Error(rpcErrorMessage(error));
  const root = (data ?? {}) as Record<string, unknown>;
  const fee = (root.platform_fee ?? null) as Record<string, unknown> | null;
  const payer = (root.payer_wallet ?? null) as Record<string, unknown> | null;
  const dWallet = (root.driver_wallet ?? null) as Record<string, unknown> | null;
  return {
    order_id: String(root.order_id ?? orderId),
    orders_driver_trip_fee:
      root.orders_driver_trip_fee != null ? Number(root.orders_driver_trip_fee) : null,
    platform_fee: fee
      ? {
          amount: Number(fee.amount ?? 0),
          payer_wallet_type: String(fee.payer_wallet_type ?? ""),
          payer_id: String(fee.payer_id ?? ""),
          created_at: String(fee.created_at ?? ""),
        }
      : null,
    driver_settlement: (root.driver_settlement ?? null) as Record<string, unknown> | null,
    payer_wallet: payer
      ? {
          wallet_type: String(payer.wallet_type ?? ""),
          owner_id: payer.owner_id != null ? String(payer.owner_id) : null,
          owner_name: payer.owner_name != null ? String(payer.owner_name) : null,
          balance: Number(payer.balance ?? 0),
          negative_credit_limit: Number(payer.negative_credit_limit ?? 0),
          alert_threshold_iqd: Number(payer.alert_threshold_iqd ?? 10000),
        }
      : null,
    driver_wallet: dWallet
      ? {
          wallet_type: String(dWallet.wallet_type ?? ""),
          balance: Number(dWallet.balance ?? 0),
        }
      : null,
  };
}

export type MarketplaceAppBanner = {
  id: string;
  title: string | null;
  highlight: string | null;
  ctaLabel: string | null;
  imageUrl: string;
  sortOrder: number;
  isActive: boolean;
};

export type MarketplaceAppCategory = {
  id: string;
  name: string;
  nameAr: string | null;
  nameKu: string | null;
  imageUrl: string;
  matchKeywords: string[];
  sortOrder: number;
  isActive: boolean;
};

export type MarketplaceAppServiceItem = {
  id: string;
  iconKey: string;
  label: string;
  labelAr: string | null;
  labelKu: string | null;
  sortOrder: number;
  isActive: boolean;
};

export type MarketplaceAppBestSeller = {
  id: string;
  tenantId: string;
  name: string;
  imageUrl: string | null;
  price: number;
  currency: string;
  restaurantName: string;
  sortOrder: number;
  isAvailable: boolean;
};

export type MarketplaceAppBestSellerCandidate = {
  id: string;
  name: string;
  tenantId: string;
  restaurantName: string;
  imageUrl: string | null;
  price: number;
  currency: string;
  isAvailable: boolean;
};

export type MarketplaceAppSection = {
  key: string;
  title: string;
  titleAr: string | null;
  titleKu: string | null;
  enabled: boolean;
  sortOrder: number;
};

export async function fetchMarketplaceAppSettings() {
  return adminFetch<{
    banners: MarketplaceAppBanner[];
    categories: MarketplaceAppCategory[];
    topRestaurants: {
      title: string;
      enabled: boolean;
      tenantIds: string[];
      items: Array<{ id: string; name: string; logoUrl: string | null }>;
    };
    serviceItems: MarketplaceAppServiceItem[];
    bestSellers: MarketplaceAppBestSeller[];
    bestSellerCandidates: MarketplaceAppBestSellerCandidate[];
    sections: MarketplaceAppSection[];
    clients: Array<{ id: string; name: string; logoUrl: string | null; status: string }>;
    mediaHints: {
      banner: { width: number; height: number; note: string };
      category: { width: number; height: number; note: string };
    };
  }>("/v1/admin/marketplace-app");
}

export async function saveMarketplaceBanner(input: {
  id?: string;
  title?: string | null;
  highlight?: string | null;
  ctaLabel?: string | null;
  imageUrl: string;
  sortOrder?: number;
  isActive?: boolean;
}) {
  return adminFetch<{ id: string }>("/v1/admin/marketplace-app/banners", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deleteMarketplaceBanner(id: string) {
  return adminFetch<{ ok: boolean }>(`/v1/admin/marketplace-app/banners/${id}`, {
    method: "DELETE",
  });
}

export async function saveMarketplaceCategory(input: {
  id?: string;
  name: string;
  nameAr?: string | null;
  nameKu?: string | null;
  imageUrl: string;
  matchKeywords?: string[];
  sortOrder?: number;
  isActive?: boolean;
}) {
  return adminFetch<{ id: string }>("/v1/admin/marketplace-app/categories", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deleteMarketplaceCategory(id: string) {
  return adminFetch<{ ok: boolean }>(`/v1/admin/marketplace-app/categories/${id}`, {
    method: "DELETE",
  });
}

export async function saveMarketplaceTopRestaurants(input: {
  title?: string;
  enabled?: boolean;
  tenantIds?: string[];
}) {
  return adminFetch<{ ok: boolean }>("/v1/admin/marketplace-app/top-restaurants", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

export async function saveMarketplaceServiceItem(input: {
  id?: string;
  iconKey: string;
  label: string;
  labelAr?: string | null;
  labelKu?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}) {
  return adminFetch<{ id: string }>("/v1/admin/marketplace-app/service-items", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deleteMarketplaceServiceItem(id: string) {
  return adminFetch<{ ok: boolean }>(`/v1/admin/marketplace-app/service-items/${id}`, {
    method: "DELETE",
  });
}

/** Replaces the pinned best-seller selection; the API validates ids against the catalogue. */
export async function saveMarketplaceBestSellers(input: { menuItemIds: string[] }) {
  return adminFetch<{ ok: boolean }>("/v1/admin/marketplace-app/best-sellers", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

/** Catalogue items of one store — drives the store-first picker. */
export async function fetchBestSellerCandidates(tenantId: string) {
  return adminFetch<MarketplaceAppBestSellerCandidate[]>(
    `/v1/admin/marketplace-app/best-seller-candidates?tenantId=${encodeURIComponent(tenantId)}`
  );
}

export async function saveMarketplaceSections(input: {
  sections: Array<{
    key: string;
    title?: string;
    titleAr?: string | null;
    titleKu?: string | null;
    enabled?: boolean;
    sortOrder?: number;
  }>;
}) {
  return adminFetch<{ ok: boolean }>("/v1/admin/marketplace-app/sections", {
    method: "PUT",
    body: JSON.stringify(input),
  });
}

/** Upload banner/category asset to public marketplace-assets bucket. */
export async function uploadMarketplaceAsset(
  file: File,
  kind: "banner" | "category" = "banner"
): Promise<string> {
  const maxBytes = 5 * 1024 * 1024;
  if (file.size > maxBytes) throw new Error("image_too_large");
  const supabase = getSupabaseClient();
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const path = `${kind}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from("marketplace-assets").upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("marketplace-assets").getPublicUrl(path);
  return data.publicUrl;
}

export async function fetchAdminMe() {
  return adminFetch<{
    userId: string;
    email?: string;
    adminId: string;
    role: string;
    displayName: string;
  }>("/v1/admin/me");
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Partners ──────────────────────────────────────────────────────────────────

export type AdminPartner = {
  id: string;
  name: string;
  slug: string;
  status: string;
  contact_email?: string | null;
  created_at: string;
  linked_tenants_count: number;
  leave_requests_count: number;
};

export type AdminPartnerLink = {
  tenant_id: string;
  tenant_name: string;
  status: string;
  dispatch_authority: string;
  is_primary_dispatcher: boolean;
  linked_at: string;
  partner_fee_type?: string | null;
  partner_fee_amount?: number | null;
  settlement_mode?: string | null;
  cash_settlement_target?: string | null;
};

export async function fetchAdminPartners(params?: {
  search?: string;
  page?: number;
  limit?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.search) qs.set("search", params.search);
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return adminFetch<{
    items: AdminPartner[];
    page: number;
    limit: number;
    total: number;
  }>(`/v1/admin/partners${suffix}`);
}

export type AdminPartnerWalletSummary = {
  balance: number;
  negative_credit_limit: number;
  alert_threshold_iqd: number;
  updated_at: string | null;
} | null;

export type AdminPartnerUserRow = {
  user_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  email: string | null;
};

export type AdminPartnerLinkRow = {
  tenant_id: string;
  tenant_name: string;
  status: string;
  dispatch_authority: string;
  is_primary_dispatcher: boolean;
  linked_by: string;
  linked_at: string | null;
};

export type AdminPartnerDetail = {
  partner: AdminPartner & {
    contact_email?: string | null;
    linked_clients_count: number;
    leave_requests_count: number;
    driver_count: number;
    active_driver_count: number;
    handled_orders_count: number;
    wallet: AdminPartnerWalletSummary;
  };
  links: AdminPartnerLinkRow[];
  users: AdminPartnerUserRow[];
};

/** SA partner view — detail row + client links + team accounts. */
export async function fetchAdminPartner(id: string) {
  return adminFetch<AdminPartnerDetail>(`/v1/admin/partners/${id}`);
}

export async function updateAdminPartnerProfile(
  id: string,
  patch: { name?: string; slug?: string; contactEmail?: string }
) {
  return adminFetch<{ ok: boolean; partner_id: string; name: string; slug: string }>(
    `/v1/admin/partners/${id}/profile`,
    { method: "PATCH", body: JSON.stringify(patch) }
  );
}

export async function updateAdminPartnerStatus(
  id: string,
  status: "active" | "suspended"
) {
  return adminFetch<{ ok: boolean; partner_id: string; status: string }>(
    `/v1/admin/partners/${id}/status`,
    { method: "PATCH", body: JSON.stringify({ status }) }
  );
}

export async function deleteAdminPartner(id: string) {
  return adminFetch<{
    ok: boolean;
    partner_id: string;
    status: string;
    removed_links: number;
    deactivated_users: number;
  }>(`/v1/admin/partners/${id}`, { method: "DELETE" });
}

export async function createAdminPartner(input: {
  name: string;
  email: string;
  slug?: string;
}) {
  return adminFetch<{
    partner: Record<string, unknown>;
    user: Record<string, unknown>;
    credentials: {
      email: string;
      temporaryPassword: string;
      mustChangePassword: boolean;
    };
  }>("/v1/admin/partners", {
    method: "POST",
    body: JSON.stringify({
      name: input.name,
      email: input.email,
      slug: input.slug,
    }),
  });
}

export type AdminPartnerPasscode = {
  ok: boolean;
  passcode: string;
  partner_id: string;
  partner: {
    id: string;
    name: string;
    slug: string;
    email: string | null;
    phone: string | null;
  };
  expires_at: string;
  expires_in_minutes: number;
};

/**
 * Mint a partner org draft + a single-use onboarding passcode.
 * The partner redeems it in the partner app / web (peek → finalize).
 * Valid 6h if unused; consumed on first successful onboarding.
 */
export async function mintAdminPartnerPasscode(input: {
  name: string;
  phone: string;
  slug?: string;
  email?: string;
  expiryMinutes?: number;
}) {
  return adminFetch<AdminPartnerPasscode>("/v1/admin/partners/passcode", {
    method: "POST",
    body: JSON.stringify({
      name: input.name,
      phone: input.phone,
      slug: input.slug,
      email: input.email,
      expiryMinutes: input.expiryMinutes,
    }),
  });
}

export async function createAdminBusinessOnboarding(input: {
  name: string;
  phone: string;
  address: string;
  /** Reserved client login username (required). */
  username: string;
  /** Optional contact email — invoices only, never a login ID. */
  email?: string;
  partnerId?: string;
  expiryMinutes?: number;
}) {
  return adminFetch<{
    passcode: string;
    tenant_id: string;
    expires_at: string;
    tenant: Record<string, unknown>;
  }>("/v1/admin/clients/onboard", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateAdminClientProfile(
  id: string,
  patch: {
    name?: string;
    phone?: string;
    address?: string;
    username?: string;
    email?: string | null;
  }
) {
  return adminFetch<{ ok: boolean; client?: Record<string, unknown> }>(
    `/v1/admin/clients/${id}/profile`,
    {
      method: "PATCH",
      body: JSON.stringify(patch),
    }
  );
}

export async function deleteAdminClient(id: string) {
  return adminFetch<{ ok: boolean; tenant_id: string; status: string }>(
    `/v1/admin/clients/${id}`,
    { method: "DELETE" }
  );
}

export async function linkAdminPartnerTenant(
  partnerId: string,
  input: {
    tenantId: string;
    dispatchAuthority?: "tenant" | "partner";
    isPrimaryDispatcher?: boolean;
  }
) {
  return adminFetch<{ link: Record<string, unknown> }>(
    `/v1/admin/partners/${partnerId}/link`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: input.tenantId,
        dispatchAuthority: input.dispatchAuthority || "partner",
        isPrimaryDispatcher: input.isPrimaryDispatcher ?? false,
      }),
    }
  );
}

export async function unlinkAdminPartnerClient(partnerId: string, tenantId: string) {
  return adminFetch<{ ok: boolean; partner_id: string; tenant_id: string }>(
    `/v1/admin/partners/${partnerId}/link`,
    { method: "DELETE", body: JSON.stringify({ tenantId }) }
  );
}

export async function setDispatchAuthority(
  partnerId: string,
  input: { tenantId: string; dispatchAuthority: "tenant" | "partner" }
) {
  return adminFetch<{ link: Record<string, unknown> }>(
    `/v1/admin/partners/${partnerId}/authority`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    }
  );
}

export async function fetchAdminPartnerLinks(partnerId: string) {
  return adminFetch<{ links: AdminPartnerLink[] }>(
    `/v1/admin/partners/${partnerId}/links`
  );
}

export async function updateAdminPartnerLinkFinance(
  partnerId: string,
  input: {
    tenantId: string;
    partnerFeeType?: "per_order" | "percentage" | null;
    partnerFeeAmount?: number | null;
    settlementMode?: "instant" | "credit" | null;
    cashSettlementTarget?: "business" | "partner" | null;
  }
) {
  return adminFetch<{ link: AdminPartnerLink }>(
    `/v1/admin/partners/${partnerId}/link-finance`,
    {
      method: "PATCH",
      body: JSON.stringify({
        tenantId: input.tenantId,
        partnerFeeType: input.partnerFeeType,
        partnerFeeAmount: input.partnerFeeAmount,
        settlementMode: input.settlementMode,
        cashSettlementTarget: input.cashSettlementTarget,
      }),
    }
  );
}

export async function approvePartnerLeave(
  partnerId: string,
  input: { tenantId: string }
) {
  return adminFetch<{ ok: boolean; link: { tenant_id: string; status: string } }>(
    `/v1/admin/partners/${partnerId}/leave/approve`,
    {
      method: "POST",
      body: JSON.stringify(input),
    }
  );
}
